import apiClient from '@/shared/api/client'
import type {
  Supplier, ItemMaster, Store, Shift,
  GateEntry, VisitorLog,
} from '@/shared/types/master'

interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  size: number
  pages: number
}

// ── Suppliers ────────────────────────────────────────────────────────────────
export const suppliersApi = {
  list: async (params?: Record<string, unknown>) => {
    const res = await apiClient.get<PaginatedResponse<Supplier>>('/suppliers', { params })
    return res.data
  },
  get: async (id: string) => (await apiClient.get<Supplier>(`/suppliers/${id}`)).data,
  create: async (data: Record<string, unknown>) =>
    (await apiClient.post<Supplier>('/suppliers', data)).data,
  update: async (id: string, data: Partial<Supplier>) =>
    (await apiClient.patch<Supplier>(`/suppliers/${id}`, data)).data,

  // ── Onboarding workflow ─────────────────────────────────────────────────
  submit: async (id: string) =>
    (await apiClient.post<Supplier>(`/suppliers/${id}/submit`)).data,

  pmAction: async (id: string, data: { status: 'APPROVED' | 'REJECTED' | 'CHANGES_REQUESTED'; remarks?: string }) =>
    (await apiClient.post<Supplier>(`/suppliers/${id}/pm-action`, data)).data,

  cfoAction: async (id: string, data: { status: 'APPROVED' | 'REJECTED'; remarks?: string }) =>
    (await apiClient.post<Supplier>(`/suppliers/${id}/cfo-action`, data)).data,

  listPendingPm: async () =>
    (await apiClient.get<Supplier[]>('/suppliers/approvals/pending-pm')).data,

  listPendingCfo: async () =>
    (await apiClient.get<Supplier[]>('/suppliers/approvals/pending-cfo')).data,

  // ── Documents ───────────────────────────────────────────────────────────
  uploadDocument: async (id: string, docType: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    const res = await apiClient.post<Supplier>(
      `/suppliers/${id}/documents/${docType}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return res.data
  },

  getDocumentUrl: (id: string, docType: string) =>
    `/api/v1/suppliers/${id}/documents/${docType}`,

  // ── Settings (admin only) ───────────────────────────────────────────────
  listSettings: async () =>
    (await apiClient.get<{ key: string; value: string; description?: string }[]>('/suppliers/settings/all')).data,

  updateSetting: async (key: string, value: string) =>
    (await apiClient.patch(`/suppliers/settings/${key}`, { value })).data,
}

// ── Items ────────────────────────────────────────────────────────────────────
export const itemsApi = {
  list: async (params?: Record<string, unknown>) => {
    const res = await apiClient.get<PaginatedResponse<ItemMaster>>('/items', { params })
    return res.data
  },
  get: async (id: string) => (await apiClient.get<ItemMaster>(`/items/${id}`)).data,
  create: async (data: Omit<ItemMaster, 'id' | 'item_code' | 'created_at'>) =>
    (await apiClient.post<ItemMaster>('/items', data)).data,
  update: async (id: string, data: Partial<ItemMaster>) =>
    (await apiClient.patch<ItemMaster>(`/items/${id}`, data)).data,
}

// ── Stores ───────────────────────────────────────────────────────────────────
export const storesApi = {
  list: async (params?: Record<string, unknown>) => {
    const res = await apiClient.get<PaginatedResponse<Store>>('/stores', { params })
    return res.data
  },
  get: async (id: string) => (await apiClient.get<Store>(`/stores/${id}`)).data,
  create: async (data: Omit<Store, 'id' | 'store_code' | 'created_at' | 'plant_name'>) =>
    (await apiClient.post<Store>('/stores', data)).data,
  update: async (id: string, data: Partial<Store>) =>
    (await apiClient.patch<Store>(`/stores/${id}`, data)).data,
}

// ── Shifts ───────────────────────────────────────────────────────────────────
export const shiftsApi = {
  list: async (params?: { plant_id?: string; is_active?: boolean }) => {
    const res = await apiClient.get<Shift[]>('/shifts', { params })
    return res.data
  },
  create: async (data: Omit<Shift, 'id' | 'plant_name' | 'created_at'>) =>
    (await apiClient.post<Shift>('/shifts', data)).data,
  update: async (id: string, data: Partial<Shift>) =>
    (await apiClient.patch<Shift>(`/shifts/${id}`, data)).data,
}

// ── Gate Entries ──────────────────────────────────────────────────────────────
export const gateEntriesApi = {
  list: async (params?: Record<string, unknown>) => {
    const res = await apiClient.get<PaginatedResponse<GateEntry>>('/gate-entries', { params })
    return res.data
  },
  get: async (id: string) => (await apiClient.get<GateEntry>(`/gate-entries/${id}`)).data,
  create: async (data: Record<string, unknown>) =>
    (await apiClient.post<GateEntry>('/gate-entries', data)).data,
  update: async (id: string, data: Record<string, unknown>) =>
    (await apiClient.patch<GateEntry>(`/gate-entries/${id}`, data)).data,
  updateStatus: async (id: string, data: { status: string; void_reason?: string }) =>
    (await apiClient.post<GateEntry>(`/gate-entries/${id}/status`, data)).data,
  todaySummary: async (plant_id: string) =>
    (await apiClient.get<Record<string, number>>('/gate-entries/today-summary', { params: { plant_id } })).data,
}


// ── Visitors ──────────────────────────────────────────────────────────────────
export const visitorsApi = {
  list: async (params?: Record<string, unknown>) => {
    const res = await apiClient.get<PaginatedResponse<VisitorLog>>('/visitors', { params })
    return res.data
  },
  get: async (id: string) => (await apiClient.get<VisitorLog>(`/visitors/${id}`)).data,
  create: async (data: Record<string, unknown>) =>
    (await apiClient.post<VisitorLog>('/visitors', data)).data,
  update: async (id: string, data: Record<string, unknown>) =>
    (await apiClient.patch<VisitorLog>(`/visitors/${id}`, data)).data,
  recordExit: async (id: string, actual_exit_time?: string) =>
    (await apiClient.post<VisitorLog>(`/visitors/${id}/exit`, { actual_exit_time })).data,
  inside: async (plant_id: string) =>
    (await apiClient.get<VisitorLog[]>('/visitors/inside', { params: { plant_id } })).data,
  // Sprint VIS-PHOTO-1 — gate selfie capture
  uploadPhoto: async (id: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    const res = await apiClient.post<VisitorLog>(
      `/visitors/${id}/photo`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return res.data
  },
  deletePhoto: async (id: string) =>
    (await apiClient.delete<VisitorLog>(`/visitors/${id}/photo`)).data,

  // ─── Sprint 0150 — Visitor approval workflow ─────────────────────
  listHosts: async (params: { plant_id?: string; search?: string } = {}) =>
    (await apiClient.get<HostOption[]>('/visitors/hosts', { params })).data,
  listPendingMyApproval: async () =>
    (await apiClient.get<VisitorLog[]>('/visitors/pending-my-approval')).data,
  approve: async (id: string) =>
    (await apiClient.post<VisitorLog>(`/visitors/${id}/approve`)).data,
  reject: async (id: string, reject_reason: string) =>
    (await apiClient.post<VisitorLog>(
      `/visitors/${id}/reject`, { reject_reason },
    )).data,
}

export interface HostOption {
  id: string
  emp_code: string
  full_name: string
  designation_name: string | null
  department_name: string | null
  photo_url: string | null
}

// ─── Sprint 0149 — Business card capture + OCR + library ────────────
export interface BusinessCardExtractResult {
  card_id: string
  image_url: string
  raw_ocr_text: string | null
  ocr_engine: string | null
  ocr_confidence: number | null
  ocr_available: boolean
  ocr_error: string | null
  extracted_name: string | null
  extracted_company: string | null
  extracted_designation: string | null
  extracted_mobile: string | null
  extracted_email: string | null
  extracted_website: string | null
  extracted_address: string | null
}

export interface BusinessCard {
  id: string
  visitor_log_id: string | null
  image_url: string | null
  raw_ocr_text: string | null
  ocr_engine: string | null
  ocr_confidence: number | null
  ocr_processed_at: string | null
  extracted_name: string | null
  extracted_company: string | null
  extracted_designation: string | null
  extracted_mobile: string | null
  extracted_email: string | null
  extracted_website: string | null
  extracted_address: string | null
  created_at: string
  updated_at: string
}

export const businessCardsApi = {
  /** Capture + OCR a card. Returns extracted fields (and persists). */
  extract: async (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    const res = await apiClient.post<BusinessCardExtractResult>(
      '/visitors/business-cards/extract', fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return res.data
  },

  list: async (params: {
    page?: number
    size?: number
    search?: string
    only_linked?: boolean
  } = {}) =>
    (await apiClient.get<{
      items: BusinessCard[]
      total: number
      page: number
      size: number
      pages: number
    }>('/visitors/business-cards', { params })).data,

  get: async (id: string) =>
    (await apiClient.get<BusinessCard>(`/visitors/business-cards/${id}`)).data,

  update: async (id: string, body: Partial<BusinessCard>) =>
    (await apiClient.patch<BusinessCard>(
      `/visitors/business-cards/${id}`, body,
    )).data,

  delete: async (id: string) => {
    await apiClient.delete(`/visitors/business-cards/${id}`)
  },

  linkToVisitor: async (visitor_id: string, card_id: string) =>
    (await apiClient.post<VisitorLog>(
      `/visitors/${visitor_id}/business-card/${card_id}`,
    )).data,
}

export interface HostOption {
  id: string
  emp_code: string
  full_name: string
  designation_name: string | null
  department_name: string | null
  photo_url: string | null
}
